import React, { useMemo } from 'react';
import { useLocation, Link } from 'react-router-dom';

const OrderSuccess = () => {
  const location = useLocation();

  // Try loading from localStorage if state is empty (supports page refresh without crashing)
  const orderDetails = useMemo(() => {
    const stateData = location.state || {};
    if (stateData.orderNumber) {
      return stateData;
    }
    const stored = localStorage.getItem('lastOrderDetails');
    if (stored) {
      try {
        return JSON.parse(stored);
      } catch (e) {
        console.error('Failed to parse lastOrderDetails from localStorage:', e);
      }
    }
    return {};
  }, [location.state]);

  const {
    orderNumber = 'N/A',
    grandTotal = 0,
    paymentMethod = 'N/A',
    whatsappUrl = ''
  } = orderDetails;

  const handleOpenWhatsAppAgain = () => {
    if (whatsappUrl) {
      window.open(whatsappUrl, '_blank');
    } else {
      alert('WhatsApp connection link is missing.');
    }
  };

  const containerStyle = {
    maxWidth: '650px',
    margin: '60px auto',
    padding: '40px 30px',
    background: 'var(--color-surface-1-rgba, rgba(24, 24, 27, 0.75))',
    border: '1px solid var(--color-border-light, rgba(255, 255, 255, 0.08))',
    borderRadius: '16px',
    boxShadow: 'var(--card-shadow, 0 20px 50px rgba(0, 0, 0, 0.5)), 0 0 40px rgba(255, 122, 0, 0.02)',
    backdropFilter: 'blur(16px)',
    webkitBackdropFilter: 'blur(16px)',
    textAlign: 'center',
    color: 'var(--color-text-primary)',
    animation: 'fadeIn 0.5s ease-out'
  };

  const checkCircleStyle = {
    width: '72px',
    height: '72px',
    borderRadius: '50%',
    background: 'rgba(16, 185, 129, 0.1)',
    border: '3px solid #10b981',
    color: '#10b981',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '2.5rem',
    fontWeight: 'bold',
    margin: '0 auto 24px auto',
    boxShadow: '0 0 20px rgba(16, 185, 129, 0.2)'
  };

  const detailsCardStyle = {
    background: 'var(--color-background, rgba(255, 255, 255, 0.02))',
    border: '1px solid var(--color-border-light, rgba(255, 255, 255, 0.04))',
    borderRadius: '12px',
    padding: '20px 24px',
    margin: '30px 0',
    textAlign: 'left',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px'
  };

  const detailRowStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderBottom: '1px solid var(--color-border-light, rgba(255, 255, 255, 0.04))',
    paddingBottom: '8px'
  };

  const buttonGroupStyle = {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    marginTop: '30px'
  };

  const primaryBtnStyle = {
    width: '100%',
    background: 'linear-gradient(135deg, #ff7a00 0%, #ea580c 100%)',
    color: 'var(--color-text-primary)',
    border: 'none',
    borderRadius: '8px',
    padding: '14px 24px',
    fontSize: '1rem',
    fontWeight: '700',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    boxShadow: '0 4px 14px rgba(255, 122, 0, 0.3)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px'
  };

  const secondaryBtnStyle = {
    width: '100%',
    background: 'var(--btn-secondary-bg, rgba(255, 255, 255, 0.05))',
    border: 'var(--btn-secondary-border, 1px solid rgba(255, 255, 255, 0.08))',
    color: 'var(--color-text-primary)',
    borderRadius: '8px',
    padding: '12px 24px',
    fontSize: '0.95rem',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    textAlign: 'center',
    display: 'block',
    textDecoration: 'none'
  };

  return (
    <div style={{ padding: '20px 15px' }}>
      <div style={containerStyle}>
        
        {/* Success Icon */}
        <div style={checkCircleStyle}>✓</div>

        <h2 style={{ fontSize: '2rem', fontWeight: '800', marginBottom: '8px', background: 'linear-gradient(to right, var(--color-gradient-heading-start, #ffffff), var(--color-gradient-heading-end, #e4e4e7))', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
          Order Created Successfully
        </h2>

        <p style={{ color: 'var(--color-text-secondary)', fontSize: '1rem', lineHeight: '1.5', maxWidth: '500px', margin: '0 auto' }}>
          Your order has been successfully submitted and is awaiting verification by the Regard Traders team.
        </p>

        {/* Order Details Breakdown */}
        <div style={detailsCardStyle}>
          <div style={detailRowStyle}>
            <span style={{ color: 'var(--color-text-secondary)', fontSize: '0.9rem' }}>Order Number</span>
            <span style={{ color: 'var(--color-text-primary)', fontWeight: '700', fontSize: '0.95rem' }}>{orderNumber}</span>
          </div>

          <div style={detailRowStyle}>
            <span style={{ color: 'var(--color-text-secondary)', fontSize: '0.9rem' }}>Order Value</span>
            <span style={{ color: '#ff7a00', fontWeight: '700', fontSize: '1.1rem' }}>
              ₹{grandTotal.toLocaleString('en-IN')}
            </span>
          </div>

          <div style={detailRowStyle}>
            <span style={{ color: 'var(--color-text-secondary)', fontSize: '0.9rem' }}>Payment Method</span>
            <span style={{ color: 'var(--color-text-primary)', fontWeight: '600', fontSize: '0.9rem' }}>{paymentMethod}</span>
          </div>

          <div style={{ ...detailRowStyle, borderBottom: 'none', paddingBottom: 0 }}>
            <span style={{ color: 'var(--color-text-secondary)', fontSize: '0.9rem' }}>Status</span>
            <span style={{
              color: '#ff7a00',
              background: 'rgba(255, 122, 0, 0.12)',
              border: '1px solid rgba(255, 122, 0, 0.25)',
              fontSize: '0.8rem',
              fontWeight: '700',
              padding: '2px 8px',
              borderRadius: '4px',
              textTransform: 'uppercase'
            }}>
              Pending
            </span>
          </div>
        </div>

        {/* Recovery & Navigation Buttons */}
        <div style={buttonGroupStyle}>
          
          <button 
            onClick={handleOpenWhatsAppAgain} 
            style={primaryBtnStyle}
            onMouseOver={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
            onMouseOut={(e) => e.currentTarget.style.transform = 'none'}
          >
            💬 Open WhatsApp Again
          </button>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', width: '100%' }}>
            
            <Link 
              to="/account?tab=orders" 
              style={secondaryBtnStyle}
              onMouseOver={(e) => e.currentTarget.style.background = 'var(--btn-secondary-hover-bg, rgba(255, 255, 255, 0.08))'}
              onMouseOut={(e) => e.currentTarget.style.background = 'var(--btn-secondary-bg, rgba(255, 255, 255, 0.05))'}
            >
              View Orders
            </Link>

            <Link 
              to="/shop" 
              style={secondaryBtnStyle}
              onMouseOver={(e) => e.currentTarget.style.background = 'var(--btn-secondary-hover-bg, rgba(255, 255, 255, 0.08))'}
              onMouseOut={(e) => e.currentTarget.style.background = 'var(--btn-secondary-bg, rgba(255, 255, 255, 0.05))'}
            >
              Continue Shopping
            </Link>
            
          </div>
          
        </div>

      </div>
    </div>
  );
};

export default OrderSuccess;
